import { ActionCandidate, EvaluatedCandidate, PlanningContext } from "@/core/battle/ai/types/ai.types";
import { PLANNING_CONFIG } from "@/core/battle/ai/utils/AIConfig";

/**
 * Adaptive pruning based on utility distribution
 * Keeps top candidates within threshold of best
 */
export class PruningStrategy {
    /**
     * Prune candidates adaptively
     * - Keeps all if ≤3 candidates
     * - Uses utility threshold based on top 2
     * - More lenient when all utilities are negative
     */
    prune(
        sortedCandidates: ActionCandidate[],
        depth: number,
        context: PlanningContext
    ): ActionCandidate[] {

        if (sortedCandidates.length <= 3) return sortedCandidates;

        // Групуємо по типу
        const groups = new Map<string, ActionCandidate[]>();
        for (const candidate of sortedCandidates) {
            const type = candidate.action.type;
            if (!groups.has(type)) groups.set(type, []);
            groups.get(type)!.push(candidate);
        }

        // До кожної групи застосовуємо threshold незалежно
        const result: ActionCandidate[] = [];
        for (const [ctype, candidates] of groups) {
            if (candidates.length <= 3) {
                result.push(...candidates);
                if (depth === 0 && PLANNING_CONFIG.debug) {
                    
                    this.logPruning(candidates, candidates[0]!.utility, ctype);
                }
                continue;
            }

            const bestUtility = candidates[0]!.utility;
            const secondBest = candidates[1]!.utility;
            
            // Визначаємо, чи маємо ми справу з "плато" (майже однаковими оцінками)
            const EPSILON = 0.001;
            const isPlateau = (bestUtility - secondBest) < EPSILON;

            let filtered: ActionCandidate[];
            let threshold = 0;

            if (isPlateau && bestUtility > 0) {
                // Якщо різниця мізерна, використовуємо Top-K (Beam Search)
                // Обмежуємо, наприклад, до 3 найкращих варіантів (або через конфіг)
                filtered = candidates.slice(0, PLANNING_CONFIG.maxZeroCandidates || 3);
            } else {
                // Стандартна логіка порогів для випадків, коли є явна різниця
                const avgTop2 = (bestUtility + secondBest) / 2;
                let threshold = avgTop2 * PLANNING_CONFIG.pruneThreshold;
                threshold = Math.min(threshold, secondBest);

                if (bestUtility < 0) {
                    threshold = Math.min(threshold, bestUtility * 1.5);
                }

                filtered = candidates.filter(c => c.utility >= threshold);
            }

            result.push(...filtered);
            if (depth === 0 && PLANNING_CONFIG.debug) {
                console.log(`[Pruning] Real affordable: ${candidates.length}, Kept: ${result.length}`, isPlateau);
                // Виклик логу має показувати те, що ми реально повертаємо
                this.logPruning(candidates, threshold, ctype);
            }
        }

        

        return result;
    }
    
    /**
     * Log pruning details for debugging
     */
    private logPruning(
        candidates: ActionCandidate[],
        threshold: number,
        ctype: string,
    ): void {
        const pruned = candidates.filter(c => c.utility >= threshold);
        
        console.log(`[Pruning] - ${ctype}`, {
            before: candidates.length,
            after: pruned.length,
            threshold: threshold.toFixed(2),
            bestUtility: candidates[0]?.utility.toFixed(2),
            worstKept: pruned[pruned.length - 1]?.utility.toFixed(2)
        });
        
        console.log('[Pruning] Top candidates:', 
            candidates.slice(0, 80).map(c => ({
                type: c.action.type,
                subtype: c.metadata?.type,
                target: c.action.type === 'move' 
                    ? (c.action as any).targetHexId 
                    : c.metadata?.targetEnemy,
                utility: c.utility.toFixed(1),
                components: c.components,
            }))
        );
    }
}